import React, { useMemo, useState, useRef, useEffect } from "react";
import cart from "../../assets/icons/shopping-bag.svg";
import { BRANDING_LOGO_URL, BRANDING_LOGO_HORIZONTAL_URL } from "constants/branding";
import menu from "../../assets/icons/menu.svg";
import { Link, useNavigate } from "react-router-dom";
import x from "../../assets/icons/x.svg";
import { useAppDispatch, useAppSelector } from "store";
import { logout } from "store/actions/auth";

const ProfileIconSvg = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M0 9.99951C0 4.47651 4.477 -0.000488281 10 -0.000488281C15.523 -0.000488281 20 4.47651 20 9.99951C20 15.5225 15.523 19.9995 10 19.9995C4.477 19.9995 0 15.5225 0 9.99951ZM13.078 12.9405L16.535 14.5115C16.864 14.6615 17.153 14.8785 17.385 15.1455C18.403 13.6865 19 11.9125 19 9.99951C19 5.02951 14.971 0.999512 10 0.999512C5.03 0.999512 1 5.02951 1 9.99951C1 11.9125 1.597 13.6865 2.616 15.1455C2.848 14.8785 3.137 14.6615 3.466 14.5115L6.923 12.9405C5.752 12.0255 5 10.6005 5 8.99951C5 6.23851 7.239 3.99951 10 3.99951C12.762 3.99951 15 6.23851 15 8.99951C15 10.6005 14.248 12.0255 13.078 12.9405ZM10 4.99951C7.791 4.99951 6 6.79051 6 8.99951C6 11.2085 7.791 12.9995 10 12.9995C12.209 12.9995 14 11.2085 14 8.99951C14 6.79051 12.209 4.99951 10 4.99951ZM10 18.9995C7.313 18.9995 4.902 17.8225 3.252 15.9555C3.406 15.7245 3.622 15.5385 3.88 15.4225L7.962 13.5665C8.584 13.8445 9.274 13.9995 10 13.9995C10.726 13.9995 11.416 13.8445 12.039 13.5665L16.121 15.4225C16.378 15.5385 16.594 15.7245 16.748 15.9555C15.099 17.8225 12.687 18.9995 10 18.9995Z"
      fill="currentColor"
    />
  </svg>
);

const NavBar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const userData = useAppSelector((store) => store.auth.userData);
  const cartCount = useAppSelector(
    (store) => (store.cart?.frames?.length || 0) + (store.cart?.contacts?.length || 0)
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    if (profileOpen) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [profileOpen]);

  const links = useMemo(() => {
    return [
      {
        type: "link",
        name: "الرئيسية",
        path: "/",
      },
      {
        type: "link",
        name: "عدسات الحماية",
        path: "/",
        isNew: true,
        isComingSoon: true,
      },
      {
        type: "link",
        name: "عروض الحرق",
        path: "/",
        rightSide: false,
        justMobile: false,
        isComingSoon: true,
      },
      {
        type: "divider",
      },
      {
        type: "link",
        name: "اطارات رجالية",
        link: "/products/frames?gender=men",
      },
      {
        type: "link",
        name: "اطارات نسائية",
        link: "/products/frames?gender=women",
      },
      {
        type: "link",
        name: "اطارات اطفال",
        link: "/products/frames?gender=kids",
      },
      {
        type: "divider",
      },
      // {
      //   type: "link",
      //   name: "اصنع نظارتك",
      //   link: "/",
      //   isNew: true,
      // },
      // {
      //   type: "link",
      //   name: "اختارلي الاطار",
      //   link: "/",
      //   isNew: true,
      // },
      // {
      //   type: "divider",
      // },
      {
        type: "link",
        name: "الدخول لحسابك",
        link: userData ? "/orders" : "/login",
        icon: (
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22C6.477 22 2 17.523 2 12ZM15.078 14.941L18.535 16.512C18.864 16.662 19.153 16.879 19.385 17.146C20.403 15.687 21 13.913 21 12C21 7.03 16.971 3 12 3C7.03 3 3 7.03 3 12C3 13.913 3.597 15.687 4.616 17.146C4.848 16.879 5.137 16.662 5.466 16.512L8.923 14.941C7.752 14.026 7 12.601 7 11C7 8.239 9.239 6 12 6C14.762 6 17 8.239 17 11C17 12.601 16.248 14.026 15.078 14.941ZM12 7C9.791 7 8 8.791 8 11C8 13.209 9.791 15 12 15C14.209 15 16 13.209 16 11C16 8.791 14.209 7 12 7ZM12 21C9.313 21 6.902 19.823 5.252 17.956C5.406 17.725 5.622 17.539 5.88 17.423L9.962 15.567C10.584 15.845 11.274 16 12 16C12.726 16 13.416 15.845 14.039 15.567L18.121 17.423C18.378 17.539 18.594 17.725 18.748 17.956C17.099 19.823 14.687 21 12 21Z"
              fill="black"
            />
          </svg>
        ),
      },
    ];
  }, [userData]);

  const handleLogout = () => {
    dispatch(logout());
    setIsOpen(false);
    navigate("/");
  };

  return (
    <>
      {/* mobile nav */}
      <div className="lg:hidden relative z-50 pt-14">
        <nav className="w-full transition-all duration-500 bg-white/75 top-0 nav-blur-bg fixed z-50 select-none">
          <div className="container flex items-center justify-between px-4 py-3.5 mx-auto">
            <Link to="/cart" className="p-1 relative" aria-label="Cart">
              <img src={cart} alt="cart" className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-white text-[10px] font-bold min-w-[16px] h-[16px] rounded-full flex items-center justify-center px-1">
                  {cartCount > 9 ? "9+" : cartCount}
                </span>
              )}
            </Link>

            <Link to="/">
              <img src={BRANDING_LOGO_URL} alt="Locoptics" loading="lazy" className="h-16 w-auto object-contain" />
            </Link>

            <button
              type="button"
              onClick={() => setIsOpen((prev) => !prev)}
              className="p-1"
              aria-label="Menu"
            >
              <img src={menu} alt="" loading="lazy" className="w-6 h-6" />
            </button>
          </div>

          {/* Drawer Overlay */}
          <div
            className={`fixed inset-0 bg-black/40 transition-opacity duration-300 z-[70] ${isOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
              }`}
            onClick={() => setIsOpen(false)}
          ></div>

          {/* Drawer Menu */}
          <div
            className={`fixed top-0 right-0 h-screen w-[85%] max-w-sm bg-white shadow-2xl transition-transform duration-300 ease-in-out z-[80] overflow-y-auto ${isOpen ? "translate-x-0" : "translate-x-full"
              }`}
          >
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-6 border-b border-light-gray">
                <h3 className="text-xl font-bold">القائمة</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 -mr-2 text-gray-500 hover:text-black transition-colors"
                >
                  <img src={x} alt="close" className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6 flex-grow">
                <ul className="space-y-6">
                  {links.map((link, index) => (
                    <li key={index}>
                      {link.type === "link" ? (
                        <Link
                          to={link.link as string || "/"}
                          onClick={() => setIsOpen(false)}
                          className="flex items-center justify-between text-lg group"
                        >
                          <div className="flex items-center gap-4">
                            {link.icon ? (
                              <span className="w-6 h-6 flex items-center justify-center">{link.icon}</span>
                            ) : null}
                            <span className="font-medium group-hover:text-primary transition-colors">
                              {link.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            {link.isComingSoon && (
                              <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-bold">
                                قريبا
                              </span>
                            )}
                            {link.isNew && !link.isComingSoon && (
                              <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold">
                                جديد
                              </span>
                            )}
                            <svg className="w-4 h-4 text-gray-400 scale-x-[-1] group-hover:-translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                        </Link>
                      ) : (
                        <hr className="border-light-gray my-2" />
                      )}
                    </li>
                  ))}
                  {userData && (
                    <li>
                      <button
                        type="button"
                        onClick={handleLogout}
                        className="flex items-center gap-4 w-full text-lg font-medium text-deep-red hover:opacity-80 transition-opacity"
                      >
                        <span className="w-6 h-6 flex items-center justify-center">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" fill="currentColor" />
                          </svg>
                        </span>
                        تسجيل الخروج
                      </button>
                    </li>
                  )}
                </ul>
              </div>

              <div className="p-8 bg-gray-50 border-t border-light-gray text-right" dir="rtl">
                <p className="text-xs text-gray-400">© 2026 Locoptics (بصريات الوكيل). جميع الحقوق محفوظة.</p>
              </div>
            </div>
          </div>
        </nav>
      </div>
      {/* desktop nav */}
      <div className="hidden lg:block relative z-50 lg:pt-32">
        <nav className="flex bg-white/75 top-0 transition-all duration-500 nav-blur-bg select-none items-center justify-between mx-auto fixed z-50 w-full px-8 py-1 sm:py-2 lg:py-3 gap-3">
          <div className="text-transparent">_______</div>
          <div className="container flex items-center justify-between mx-auto">
            <Link to="/" className="shrink-0">
              <img src={BRANDING_LOGO_HORIZONTAL_URL} alt="Locoptics" loading="lazy" className="block h-24 w-auto object-contain" />
            </Link>
            <ul className="flex items-center gap-16">
              <li>
                <Link to="/" className="whitespace-nowrap text-md font-medium text-gray-600 hover:text-primary transition-colors">
                  الرئيسية
                </Link>
              </li>
              <li>
                <Link to="/products/frames" className="whitespace-nowrap text-md font-medium text-gray-600 hover:text-primary transition-colors">
                  الاطارات
                </Link>
              </li>
              <li>
                <Link to="/products/contacts" className="whitespace-nowrap text-md font-medium text-gray-600 hover:text-primary transition-colors">
                  العدسات اللاصقة
                </Link>
              </li>
              <li>
                <Link to="/products/frames?category=protective" className="whitespace-nowrap text-md font-medium text-gray-600 hover:text-primary transition-colors">
                  عدسات الحماية
                </Link>
              </li>
              <li>
                <Link to="/products/frames?category=promo" className="whitespace-nowrap text-md font-medium text-gray-600 hover:text-primary transition-colors">
                  عروض الحرق
                </Link>
              </li>
            </ul>
          </div>
          <div className="flex items-center gap-8" ref={profileRef}>
            {userData ? (
              <div className="hidden lg:block relative">
                <button
                  type="button"
                  onClick={() => setProfileOpen((prev) => !prev)}
                  className="flex items-center justify-center w-9 h-9 rounded-full text-gray-600 hover:text-primary hover:bg-white/80 transition-all"
                  aria-label="الحساب"
                  aria-expanded={profileOpen}
                >
                  <ProfileIconSvg />
                </button>
                {profileOpen && (
                  <div
                    className="absolute left-0 top-full mt-2 w-72 rounded-2xl border border-[#e6e6e6] bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)] overflow-hidden z-[60]"
                    dir="rtl"
                  >
                    {/* User info */}
                    <div className="p-4 border-b border-[#e6e6e6] bg-gray-50/50">
                      <p className="text-sm font-bold text-black truncate">{userData.name || "المستخدم"}</p>
                      {userData.phone_number && (
                        <p className="text-xs text-gray-500 mt-0.5">{userData.phone_number}</p>
                      )}
                    </div>
                    {/* Saved Rx – hidden for now */}
                    {false && userData.rxes && userData.rxes.length > 0 && (
                      <div className="border-b border-[#e6e6e6]">
                        <Link
                          to="/select-lenses/prescription-lenses"
                          onClick={() => setProfileOpen(false)}
                          className="flex items-center justify-between gap-2 px-4 py-3 text-sm font-medium text-gray-600 hover:text-primary hover:bg-gray-50 transition-colors"
                        >
                          <span>كشوفاتي المحفوظة</span>
                          <span className="text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5">
                            {userData.rxes.length}
                          </span>
                        </Link>
                      </div>
                    )}
                    {/* Orders */}
                    <div className="border-b border-[#e6e6e6]">
                      <Link
                        to="/orders"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center justify-between gap-2 px-4 py-3 text-sm font-medium text-gray-600 hover:text-primary hover:bg-gray-50 transition-colors"
                      >
                        <span>طلباتي</span>
                        <svg className="w-4 h-4 text-gray-400 scale-x-[-1]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </Link>
                    </div>
                    {/* Logout */}
                    <button
                      type="button"
                      onClick={() => {
                        setProfileOpen(false);
                        dispatch(logout());
                        navigate("/");
                      }}
                      className="flex w-full items-center gap-2 px-4 py-3 text-sm font-medium text-deep-red hover:bg-red-50 transition-colors"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" className="shrink-0">
                        <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5-5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z" fill="currentColor" />
                      </svg>
                      تسجيل الخروج
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="hidden lg:flex items-center justify-center w-9 h-9 rounded-full text-gray-600 hover:text-primary hover:bg-white/80 transition-all" aria-label="تسجيل الدخول">
                <ProfileIconSvg />
              </Link>
            )}
            <Link to="/cart" className="relative">
              <img src={cart} alt="cart" className="object-cover" loading="lazy" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-primary text-white text-[10px] font-bold min-w-[18px] h-[18px] rounded-full flex items-center justify-center">
                  {cartCount > 99 ? "99+" : cartCount}
                </span>
              )}
            </Link>
          </div>
        </nav>
      </div>
    </>
  );
};

export default NavBar;
